/**
 * Send advertorial events directly to GA4 via gtag (no GTM required).
 *
 * Set VUE_APP_GA4_MEASUREMENT_ID (G-XXXXXXXX) in the env for each build.
 */

const { VUE_APP_GA4_MEASUREMENT_ID, NODE_ENV } = process.env;
const debug = NODE_ENV !== 'production';
const measurementId = VUE_APP_GA4_MEASUREMENT_ID || '';

function isDebugMode() {
	if (debug) return true;
	try {
		return new URL(window.location.href).searchParams.get('ga_debug') === '1';
	} catch (e) {
		return false;
	}
}

function ensureGtag() {
	window.dataLayer = window.dataLayer || [];
	if (!window.gtag) {
		window.gtag = function gtag() {
			window.dataLayer.push(arguments);
		};
	}
	return window.gtag;
}

/**
 * Load gtag.js and configure the GA4 property.
 */
function initGa4() {
	if (!measurementId) {
		console.warn('GA4', 'VUE_APP_GA4_MEASUREMENT_ID is not set — advertorial events will not be sent');
		return;
	}
	if (typeof window === 'undefined') return;

	const gtag = ensureGtag();

	const existing = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`);
	if (!existing) {
		const script = document.createElement('script');
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
		document.head.appendChild(script);
	}

	const debugMode = isDebugMode();
	gtag('js', new Date());
	gtag('config', measurementId, {
		...(debugMode ? { debug_mode: true } : {}),
	});
	debug && console.log('GA4', 'configured', measurementId, { debugMode });
}

/**
 * @param {string} eventName
 * @param {Record<string, string|number|undefined>} params
 */
function trackEvent(eventName, params = {}) {
	if (!measurementId) {
		debug && console.warn('GA4', 'skip event — no measurement ID', eventName, params);
		return;
	}
	const gtag = ensureGtag();
	const payload = {
		...params,
		send_to: measurementId,
		...(isDebugMode() ? { debug_mode: true } : {}),
	};
	gtag('event', eventName, payload);
	debug && console.log('GA4', 'event', eventName, payload);
}

/**
 * @param {'left'|'right'} side
 * @param {{ tenantId?: string, brandName?: string, adUrl?: string, pageRoute?: string }} meta
 */
function trackAdvertorialImpression(side, meta = {}) {
	trackEvent('advertorial_impression', {
		advertorial_side: side,
		tenant_id: meta.tenantId || '',
		brand_name: meta.brandName || '',
		ad_url: meta.adUrl || '',
		page_route: meta.pageRoute || '',
	});
}

/**
 * @param {'left'|'right'} side
 * @param {{ tenantId?: string, brandName?: string, adUrl?: string, pageRoute?: string }} meta
 */
function trackAdvertorialClick(side, meta = {}) {
	trackEvent('advertorial_click', {
		advertorial_side: side,
		tenant_id: meta.tenantId || '',
		brand_name: meta.brandName || '',
		ad_url: meta.adUrl || '',
		page_route: meta.pageRoute || '',
	});
}

export const Gtm = {
	initGa4,
	trackEvent,
	trackAdvertorialImpression,
	trackAdvertorialClick,
};
